/**
 * Manager-only dashboard banner shown in the last 30 days of the holiday year
 * listing staff whose unused leave is above the carry-over cap. Backed by
 * /api/leave/year-close-nudge which controls whether the banner appears at all.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";

const KEY = "ayobell.year_close_nudge_dismissed";

export default function YearCloseNudge() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/leave/year-close-nudge").then((r) => setData(r.data)).catch(() => setData(null));
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const { day } = JSON.parse(raw);
        if (day === new Date().toISOString().slice(0, 10)) setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(KEY, JSON.stringify({ day: new Date().toISOString().slice(0, 10) })); } catch { /* ignore */ }
  };
  if (!data || !data.active || dismissed || data.at_risk_count === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 flex items-start gap-3" data-testid="year-close-nudge">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          Holiday year closes in {data.days_to_year_end} day{data.days_to_year_end === 1 ? "" : "s"} — {data.at_risk_count} staff hold leave over the carry-over cap
        </div>
        <div className="text-xs mt-1">
          Anything above <b>{data.max_carry_over_days}</b> days will be lost when the year rolls over on <b>{data.year_end}</b>. Encourage staff to book time off or approve carry-over.
        </div>
        <ul className="text-xs mt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1" data-testid="year-close-list">
          {data.at_risk.slice(0, 6).map((s) => (
            <li key={s.staff_user_id} className="flex items-center justify-between bg-white/60 border border-amber-200 px-2 py-1">
              <Link to={`/staff/${s.staff_user_id}`} className="truncate mr-2 hover:underline">{s.staff_name}</Link>
              <span className="tabular-nums text-amber-900 whitespace-nowrap">
                <b>{s.unused}</b> unused · <b>{s.will_be_lost}</b> at risk
              </span>
            </li>
          ))}
        </ul>
        {data.at_risk_count > 6 && (
          <Link to="/time-off-requests" state={{ tab: "balances" }} className="text-xs mt-2 inline-flex items-center gap-0.5 text-amber-900 hover:underline" data-testid="year-close-view-all">
            View all {data.at_risk_count} in Leave Balances <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <button className="text-amber-800 hover:text-amber-900" onClick={dismiss} data-testid="year-close-dismiss" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
